import {
  DEFAULT_TROPHY_ICON_PATHS,
  TROPHY_ICON_PATHS,
  TROPHY_LOCKED_PALETTE,
  TROPHY_TIER_PALETTE,
  TROPHY_TRACK_COLOR,
} from "MVVM/Assets/Trophies";
import {
  ITrophy,
  ITrophyDefinition,
  ITrophyProgress,
  TrophyTier,
} from "MVVM/@types/trophies";

export const TIER_ORDER: TrophyTier[] = [
  TrophyTier.BRONZE,
  TrophyTier.SILVER,
  TrophyTier.GOLD,
  TrophyTier.PLATINUM,
  TrophyTier.DIAMOND,
];

export const TROPHY_TIER_LABEL: Record<TrophyTier, string> = {
  [TrophyTier.BRONZE]: "Bronze",
  [TrophyTier.SILVER]: "Silver",
  [TrophyTier.GOLD]: "Gold",
  [TrophyTier.PLATINUM]: "Platinum",
  [TrophyTier.DIAMOND]: "Diamond",
};

export const getTrophyTierColor = (tier: TrophyTier | undefined): string =>
  tier ? TROPHY_TIER_PALETTE[tier].color : TROPHY_LOCKED_PALETTE.color;

// Full ring/disk color set for a badge — locked (or tier-less) trophies use
// the flat gray palette regardless of which tier they'd eventually land on.
export const getTrophyPalette = (
  tier: TrophyTier | undefined,
  locked: boolean,
): { color: string; deep: string; track: string } => {
  if (locked || !tier) return TROPHY_LOCKED_PALETTE;
  return { ...TROPHY_TIER_PALETTE[tier], track: TROPHY_TRACK_COLOR };
};

export const getTrophyIconPaths = (key: string): string[] =>
  TROPHY_ICON_PATHS[key as keyof typeof TROPHY_ICON_PATHS] ??
  DEFAULT_TROPHY_ICON_PATHS;

// The API only exposes a trophy's raw metric value (trophy.value) — combine
// it with the matching TrophyDefinition's thresholds to figure out how far
// the trophy is toward its next tier. Returns null when there's nothing to
// show (no value yet, no matching definition, or already at DIAMOND).
export const getTrophyProgress = (
  trophy: Pick<ITrophy, "key" | "tier" | "value">,
  definitions: ITrophyDefinition[],
): ITrophyProgress | null => {
  if (trophy.value == null) return null;

  const nextTier = TIER_ORDER[TIER_ORDER.indexOf(trophy.tier) + 1] ?? null;
  if (!nextTier) return null;

  const definition = definitions.find((d) => d.key === trophy.key);
  const target = definition?.thresholds[nextTier.toLowerCase()];
  if (target == null) return null;

  return { current: trophy.value, target, nextTier };
};

// 0 whenever progress data isn't available so the ring just renders empty
// rather than guessing.
export const getTrophyProgressPercent = (
  progress: ITrophyProgress | null | undefined,
): number => {
  if (!progress || !progress.target) return 0;
  return Math.max(0, Math.min(100, (progress.current / progress.target) * 100));
};

// e.g. "3/10 until next tier (Diamond)" — null when there's nothing
// meaningful to show (no progress data, or already at the top tier).
export const getTrophyProgressLabel = (
  progress: ITrophyProgress | null | undefined,
): string | null => {
  if (!progress || !progress.nextTier) return null;
  return `${progress.current}/${progress.target} until next tier (${
    TROPHY_TIER_LABEL[progress.nextTier]
  })`;
};

// Two of trophySetFeatured's error codes are confirmed (LIMIT_EXCEEDED,
// NOT_FOUND); a third exists but wasn't legible in the source spec, so
// anything unrecognized falls back to the server's own message rather than
// guessing a code name.
const TROPHY_ERROR_MESSAGES: Record<string, string> = {
  LIMIT_EXCEEDED: "You can only feature up to 3 trophies.",
  NOT_FOUND: "That trophy hasn't been earned yet.",
};

export const formatTrophyError = (error: {
  code: string;
  message: string;
}): string => TROPHY_ERROR_MESSAGES[error.code] ?? error.message;
